import React, { useState } from 'react';
import { Unplug, ChevronDown, CheckCircle2, Timer, Shield } from 'lucide-react';

interface IntegrationGroup {
  id: string;
  label: string;
  active: { name: string; type: string }[];
  pending: { name: string; type: string }[];
}

export const Integrations: React.FC = () => {
  // Set initial state to null so all accordion groups are closed by default
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const groups: IntegrationGroup[] = [
    {
      id: 'erp',
      label: 'ERP & Core Operations',
      active: [
        { name: 'SAP S/4HANA', type: 'Direct API' },
        { name: 'Oracle Cloud', type: 'OIC Connector' },
        { name: 'NetSuite', type: 'SuiteTalk' },
        { name: 'Microsoft Dynamics', type: 'Dataverse' }
      ],
      pending: [
        { name: 'Odoo', type: 'On Roadmap' },
        { name: 'Epicor', type: 'On Roadmap' }
      ]
    },
    {
      id: 'data',
      label: 'Cloud & Data Intelligence',
      active: [
        { name: 'AWS S3/RDS', type: 'Private Link' },
        { name: 'Google Cloud', type: 'Native' },
        { name: 'PostgreSQL', type: 'Direct Sync' }
      ],
      pending: [
        { name: 'Snowflake', type: 'Q4 2026' },
        { name: 'MongoDB', type: 'Q3 2026' },
        { name: 'Azure Synapse', type: 'In Dev' }
      ]
    },
    {
      id: 'engagement',
      label: 'Engagement & Collaboration',
      active: [
        { name: 'Notion', type: 'Direct API' },
        { name: 'Slack Enterprise', type: 'Bot API' },
        { name: 'Jira Service Management', type: 'Webhook' },
        { name: 'Zendesk', type: 'Direct' }
      ],
      pending: [
        { name: 'HubSpot', type: 'Beta' },
        { name: 'ServiceNow', type: 'Q4 2026' }
      ]
    },
    {
      id: 'security',
      label: 'Security & Deployment',
      active: [
        { name: 'SOC2 Type II', type: 'Certified' },
        { name: 'Multi-region deployment', type: 'Active' },
        { name: 'Audit logging', type: 'Persistent' },
        { name: 'Zero-Data Persistence', type: 'Verified' }
      ],
      pending: [
        { name: 'API-level access control', type: 'Q2 2026' },
        { name: 'HIPAA Compliance', type: 'Q1 2027' },
        { name: 'AES-256 Encryption', type: 'Q4 2026' },
        { name: 'Role-based permissions', type: 'On Roadmap' }
      ]
    }
  ];

  return (
    <section id="integrations" className="py-32 bg-zodiac text-white overflow-hidden relative border-y border-white/5">
      {/* Background Depth Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[600px] bg-accent rounded-full blur-[180px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          
          {/* Left: Strategic Context */}
          <div className="lg:col-span-5 text-left lg:sticky lg:top-32">
            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full mb-8">
              <Unplug className="w-3 h-3 text-accent" />
              <p className="text-[9px] font-black uppercase tracking-[0.4em] text-accent">Unified Connector Matrix</p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter leading-tight mb-8">
              Non-Invasive <br/><span className="text-accent italic">Infrastructure Overlay</span>
            </h2>
            <p className="text-lg text-white/40 font-light leading-relaxed mb-12 max-w-md">
              Groflex extracts signals and governs action across your legacy and modern stack without disrupting existing workflows.
            </p>
            
            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-white/5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#22C55E]"></div>
                  <span className="text-[10px] font-black text-white/80 uppercase tracking-widest">Verified</span>
                </div>
                <p className="text-xs text-white/30 font-medium">Production-ready modules with bi-directional sync.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></div>
                  <span className="text-[10px] font-black text-white/80 uppercase tracking-widest">Roadmap</span>
                </div>
                <p className="text-xs text-white/30 font-medium">Currently in security audit or active development phase.</p>
              </div>
            </div>
          </div>

          {/* Right: The Accordion Control Matrix */}
          <div className="lg:col-span-7">
            {/* Scrollable Container with constrained height and hidden scrollbar */}
            <div className="space-y-4 max-h-[650px] overflow-y-auto pr-4 no-scrollbar border-b border-white/5 pb-8">
              {groups.map((group) => {
                const isOpen = openGroup === group.id;
                return (
                  <div 
                    key={group.id} 
                    className={`border rounded-[2.5rem] transition-all duration-500 overflow-hidden ${
                      isOpen ? 'bg-white/[0.04] border-white/20 shadow-2xl' : 'bg-transparent border-white/5 hover:border-white/10'
                    }`}
                  >
                    <button 
                      onClick={() => setOpenGroup(isOpen ? null : group.id)}
                      className="w-full px-10 py-8 flex justify-between items-center group/btn text-left"
                    >
                      <span className={`text-xl font-bold tracking-tight transition-colors duration-500 uppercase ${isOpen ? 'text-white' : 'text-white/60 group-hover/btn:text-white'}`}>
                        {group.label}
                      </span>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-500 ${isOpen ? 'rotate-180 text-accent' : 'text-white/20'}`} />
                    </button>

                    <div className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[900px] opacity-100 pb-12 px-10' : 'max-h-0 opacity-0 invisible'}`}>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Active Items */}
                        {group.active.map((item, idx) => (
                          <div key={idx} className="bg-white/5 rounded-2xl p-5 border border-white/5 flex items-center justify-between group/item hover:bg-white/10 transition-colors">
                            <div>
                              <p className="text-[13px] font-black text-white/90 uppercase tracking-tight mb-0.5">{item.name}</p>
                              <p className="text-[9px] font-bold text-white/20 uppercase tracking-[0.2em]">{item.type}</p>
                            </div>
                            <div className="flex items-center gap-2">
                               <CheckCircle2 className="w-4 h-4 text-[#22C55E] shadow-[0_0_10px_rgba(34,197,94,0.3)]" />
                            </div>
                          </div>
                        ))}
                        
                        {/* Pending Items */}
                        {group.pending.map((item, idx) => (
                          <div key={idx} className="bg-transparent rounded-2xl p-5 border border-dashed border-white/10 flex items-center justify-between opacity-50">
                            <div>
                              <p className="text-[13px] font-black text-white/40 uppercase tracking-tight italic mb-0.5">{item.name}</p>
                              <p className="text-[9px] font-bold text-white/20 uppercase tracking-[0.2em]">{item.type}</p>
                            </div>
                            <Timer className="w-4 h-4 text-orange-400 animate-pulse" />
                          </div>
                        ))}
                      </div>
                      
                      {/* Inner Group Footer Labeling */}
                      <div className="mt-8 pt-6 border-t border-white/5 flex items-center gap-4">
                        <Shield className="w-3 h-3 text-accent/40" />
                        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white/20">Governance-Locked Connection Architecture</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="pt-8 px-4 flex justify-between items-center opacity-40">
               <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/50 italic">Scroll for more connectors</p>
               <div className="flex gap-4">
                 <div className="w-1.5 h-1.5 rounded-full bg-white/20"></div>
                 <div className="w-1.5 h-1.5 rounded-full bg-white/20"></div>
                 <div className="w-1.5 h-1.5 rounded-full bg-white/20"></div>
               </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};